import React, { useState } from "react";
import { adminApi } from "./api";
import { toast } from "sonner";

const STAGES = [
  { key: "new", label: "New", chip: "ax-chip ax-chip-info" },
  { key: "contacted", label: "Contacted", chip: "ax-chip" },
  { key: "qualified", label: "Qualified", chip: "ax-chip ax-chip-warn" },
  { key: "proposal", label: "Proposal", chip: "ax-chip ax-chip-gold" },
  { key: "won", label: "Won", chip: "ax-chip ax-chip-success" },
  { key: "lost", label: "Lost", chip: "ax-chip ax-chip-danger" },
];

export default function CRMKanban({ leads, onEdit, onChanged }) {
  const [dragging, setDragging] = useState(null); // lead id
  const [overStage, setOverStage] = useState("");

  const grouped = STAGES.reduce((acc, s) => {
    acc[s.key] = leads.filter((l) => (l.stage || "new") === s.key);
    return acc;
  }, {});

  const onDragStart = (lead) => (e) => {
    setDragging(lead.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", lead.id);
  };
  const onDragEnd = () => {
    setDragging(null);
    setOverStage("");
  };
  const onDragOver = (stageKey) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overStage !== stageKey) setOverStage(stageKey);
  };
  const onDrop = (stageKey) => async (e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragging;
    setDragging(null);
    setOverStage("");
    if (!id) return;
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.stage === stageKey) return;
    try {
      const payload = { ...lead, stage: stageKey };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      await adminApi.put(`/leads/${id}`, payload);
      toast.success(`Moved to ${stageKey}`);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not move lead");
    }
  };

  return (
    <div className="ax-scroll overflow-x-auto pb-4" data-testid="crm-kanban">
      <div className="flex gap-4 min-w-max">
        {STAGES.map((s) => {
          const list = grouped[s.key] || [];
          const isOver = overStage === s.key;
          return (
            <div
              key={s.key}
              className="w-[300px] shrink-0 ax-glass p-3 flex flex-col"
              style={{
                borderRadius: 14,
                background: isOver ? "rgba(212,175,55,0.06)" : undefined,
                borderColor: isOver ? "rgba(212,175,55,0.4)" : undefined,
                minHeight: 380,
                transition: "background 160ms ease, border-color 160ms ease",
              }}
              onDragOver={onDragOver(s.key)}
              onDrop={onDrop(s.key)}
              onDragLeave={() => setOverStage("")}
              data-testid={`kanban-col-${s.key}`}
            >
              <div className="flex items-center justify-between px-1.5 pb-2.5 mb-2" style={{ borderBottom: "1px solid var(--ax-border)" }}>
                <div className="flex items-center gap-2">
                  <span className={s.chip}>{s.label}</span>
                </div>
                <span className="text-[10.5px] ax-mono" style={{ color: "var(--ax-text-muted)" }}>
                  {list.length}
                </span>
              </div>
              <div className="space-y-2 flex-1 ax-scroll overflow-y-auto pr-0.5">
                {list.length === 0 && (
                  <div className="text-[11px] text-center py-6" style={{ color: "var(--ax-text-muted)" }}>
                    Drop leads here
                  </div>
                )}
                {list.map((lead) => (
                  <button
                    type="button"
                    key={lead.id}
                    draggable
                    onDragStart={onDragStart(lead)}
                    onDragEnd={onDragEnd}
                    onClick={() => onEdit && onEdit(lead)}
                    className="block w-full text-left rounded-lg p-3 hover:border-[rgba(212,175,55,0.32)] transition-colors"
                    style={{
                      background: "rgba(255,255,255,0.02)",
                      border: "1px solid var(--ax-border)",
                      opacity: dragging === lead.id ? 0.4 : 1,
                      cursor: "grab",
                    }}
                    data-testid={`kanban-card-${lead.id}`}
                  >
                    <div className="text-sm font-medium truncate">{lead.name}</div>
                    <div className="text-[11px] mt-1 ax-mono" style={{ color: "var(--ax-text-muted)" }}>
                      {lead.phone || lead.email || "—"}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10.5px]" style={{ color: "var(--ax-text-dim)" }}>
                        {lead.city || "—"}
                      </span>
                      {lead.budget && (
                        <span className="text-[10.5px] ax-mono" style={{ color: "var(--ax-gold)" }}>
                          {lead.budget}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
